import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseService } from 'shared-lib';
import { SERVICE, TOOLS_CATEGORY_LIST, urlDataObject } from '../core/utility/constants';
import { CryptUtilService } from 'src/app/shared/services/crypt-util.service';
import { PsgSidebarService } from './psg-sidebar.service';
import { PubsubService } from '../core/services/pubsub.service';
import { I } from '@angular/cdk/keycodes';
import { environment } from 'src/environments/environment';
import { saveAs } from 'file-saver';
import { of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { ISaveToolDetails } from '@projects/projectsummary/src/app/shared/model/toolDetail.model';

@Injectable({
  providedIn: 'root',
})
export class PsgService {
  invokeGenerateScopeFunction = new EventEmitter();
  private changeRequestCache: Map<string, Observable<any>> = new Map();
  private rejectedMsg: BehaviorSubject<string>;
  private activePackages: BehaviorSubject<any>;
  public updateScopeNotify = new Subject<boolean>();
  private subphaseData: BehaviorSubject<any>;
  public isExternalUserDataUpdated: BehaviorSubject<boolean>;
  private isProjectScopeGenerated: BehaviorSubject<boolean>;
  public CRData: BehaviorSubject<any>;
  public projectData: BehaviorSubject<any>;
  public displayTooltipText: BehaviorSubject<any>;
  public selectedProjectMenu: BehaviorSubject<any>;
  public psgPackagePreference: BehaviorSubject<any>;
  public projectId: Subject<any>;
  public projectStatus: BehaviorSubject<any>;
  public isDraftCheck: BehaviorSubject<any>;
  public activeMenu: Subject<any>;
  public completedMenu: BehaviorSubject<any>;
  public scopeDetailsUpdated: Subject<any>;
  public initialDataSet: BehaviorSubject<any>;
  private projectMicroservice: string = environment.API_MICROSERVICE_URL.PROJECT;
  private scopeDetails: BehaviorSubject<any>;
  private packageMappingDetails: BehaviorSubject<any>;
  private dfteToolsData: BehaviorSubject<any>;
  syncMsg: BehaviorSubject<string>;
  showSyncUpdatesBanner: BehaviorSubject<boolean>;
  activityRedirectData: BehaviorSubject<any>;

  // Project rerun state management
  private projectRerunState: BehaviorSubject<boolean>;

  // Generate scope button state management
  private generateScopeButtonDisabled: BehaviorSubject<boolean>;

  // Edit mode state - when navigating from project-creation-initial in edit mode
  // This is session-only (not persisted) and cleared on browser refresh
  private editFromInitialSubject: BehaviorSubject<boolean>;

  // Package ID from initial project creation - used in PSG header
  private initialPackageIdSubject: BehaviorSubject<number | null>;

  // Package Name from initial project creation - used in project component
  private initialPackageNameSubject: BehaviorSubject<string | null>;

  // Track if client data was saved after package change - prevents re-showing blank form on navigation
  private clientSavedForPackageChangeSubject: BehaviorSubject<boolean>;

  private projectGotApproved: BehaviorSubject<any>;

  stepList = [];
  stepDetails: string;
  private userMicroserviceUrl: string;
  API_MICROSERVICE_ADMIN : string = environment.API_MICROSERVICE_URL.ADMIN;

  constructor(
    private baseService: BaseService,
    private cryptUtilService: CryptUtilService,
    private router: Router,
    private psgIndexing: PsgSidebarService,
    private pubsubService: PubsubService,
  ) {
    this.invokeGenerateScopeFunction = new EventEmitter();
    this.activePackages = new BehaviorSubject({ package: [] });
    this.subphaseData = new BehaviorSubject(false);
    this.isExternalUserDataUpdated = new BehaviorSubject(false);
    this.CRData = new BehaviorSubject(null);
    this.projectData = new BehaviorSubject(null);
    this.displayTooltipText = new BehaviorSubject(null);
    this.isProjectScopeGenerated = new BehaviorSubject(false);
    this.userMicroserviceUrl = environment.API_MICROSERVICE_URL?.USER;
    this.selectedProjectMenu = new BehaviorSubject(null);
    this.psgPackagePreference = new BehaviorSubject(null);
    this.projectId = new Subject();
    this.rejectedMsg = new BehaviorSubject(null);
    this.projectStatus = new BehaviorSubject(null);
    this.isDraftCheck = new BehaviorSubject(null);
    this.activeMenu = new Subject();
    this.completedMenu = new BehaviorSubject(null);
    this.scopeDetailsUpdated = new Subject();
    this.initialDataSet = new BehaviorSubject(null);
    this.scopeDetails = new BehaviorSubject(null);
    this.packageMappingDetails = new BehaviorSubject(null);
    this.dfteToolsData = new BehaviorSubject(null);
    this.syncMsg = new BehaviorSubject(null);
    this.showSyncUpdatesBanner = new BehaviorSubject(null);
    this.activityRedirectData = new BehaviorSubject(null);
    this.projectRerunState = new BehaviorSubject(false);
    this.generateScopeButtonDisabled = new BehaviorSubject(true);
    this.editFromInitialSubject = new BehaviorSubject(false);
    this.initialPackageIdSubject = new BehaviorSubject<number | null>(null);
    this.initialPackageNameSubject = new BehaviorSubject<string | null>(null);
    this.clientSavedForPackageChangeSubject = new BehaviorSubject<boolean>(false);
    this.projectGotApproved = new BehaviorSubject(null);
  }

  /**
   * GetPmgSurveyStatus: Fetches the PMG survey status for a project.
   *
   * @param projectId - The project ID to check.
   * @param isMasterProject - Flag to determine v1/v2 API routing.
   * @returns Observable with the survey status data.
   */
  getPmgSurveyStatus(projectId: string, isMasterProject: boolean = false): Observable<any> {
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.get(`/psg/pmg-survey-status/${projectId}`, apiUrl);
  }

  getProjectGotApproved(): Observable<any> {
    return this.projectGotApproved.asObservable();
  }

  setProjectGotApproved(data) {
    this.projectGotApproved.next(data);
  }

  /**
   * Get editFromInitial value synchronously
   * Returns true when navigating from project-creation-initial in edit mode
   */
  isEditFromInitialMode(): boolean {
    return this.editFromInitialSubject.value;
  }

  /**
   * Set editFromInitial flag - call when navigating from project-creation-initial in edit mode
   */
  setEditFromInitialMode(value: boolean): void {
    this.editFromInitialSubject.next(value);
  }

  /**
   * Clear editFromInitial flag - call when leaving PSG or completing setup
   */
  clearEditFromInitialMode(): void {
    this.editFromInitialSubject.next(false);
  }

  /**
   * Set initial package ID - call when navigating from project-creation-initial
   */
  setInitialPackageId(packageId: number | null): void {
    this.initialPackageIdSubject.next(packageId);
  }

  /**
   * Get initial package ID value synchronously
   */
  getInitialPackageIdValue(): number | null {
    return this.initialPackageIdSubject.value;
  }

  /**
   * Get initial package ID as observable - use in PSG header for subscription
   */
  getInitialPackageId$(): Observable<number | null> {
    return this.initialPackageIdSubject.asObservable();
  }

  /**
   * Clear initial package ID
   */
  clearInitialPackageId(): void {
    this.initialPackageIdSubject.next(null);
    // Also clear client saved flag when clearing package ID
    this.clientSavedForPackageChangeSubject.next(false);
  }

  /**
   * Set flag indicating client data was saved after package change
   */
  setClientSavedForPackageChange(saved: boolean): void {
    this.clientSavedForPackageChangeSubject.next(saved);
  }

  /**
   * Check if client data was already saved after package change
   */
  isClientSavedForPackageChange(): boolean {
    return this.clientSavedForPackageChangeSubject.value;
  }

  /**
   * Set initial package name - call when navigating from project-creation-initial
   */
  setInitialPackageName(packageName: string | null): void {
    this.initialPackageNameSubject.next(packageName);
  }

  /**
   * Get initial package name value synchronously
   */
  getInitialPackageNameValue(): string | null {
    return this.initialPackageNameSubject.value;
  }

  /**
   * Clear initial package name
   */
  clearInitialPackageName(): void {
    this.initialPackageNameSubject.next(null);
  }

  /**
   * GetProjectRerunStatus: Returns an observable for the project rerun state.
   *
   * @returns Observable<boolean> emitting the current rerun state
   */
  getProjectRerunStatus(): Observable<boolean> {
    return this.projectRerunState.asObservable();
  }

  /**
   * CheckAndUpdateProjectRerunStatus: Checks the project rerun status via API and updates the state.
   *
   * @param projectId - The project ID to check.
   * @param isMasterProject - Flag to determine v1/v2 API routing.
   */
  checkAndUpdateProjectRerunStatus(projectId: string, isMasterProject: boolean = false): void {
    this.getPmgSurveyStatus(projectId, isMasterProject).subscribe({
      next: (response) => {
        // If response.data exists and is not empty, project is in rerun state
        const isRerun = response?.data && Object.keys(response.data).length > 0;
        this.projectRerunState.next(isRerun);
      },
      error: (error) => {
        console.error('Error fetching PMG survey status:', error);
        // Default to false on error to prevent UI issues
        this.projectRerunState.next(false);
      }
    });
  }

  /**
   * GetGenerateScopeButtonDisabled: Returns an observable for the generate scope button disabled state.
   *
   * @returns Observable<boolean> emitting the current button disabled state
   */
  getGenerateScopeButtonDisabled(): Observable<boolean> {
    return this.generateScopeButtonDisabled.asObservable();
  }

  /**
   * SetGenerateScopeButtonDisabled: Updates the generate scope button disabled state.
   *
   * @param isDisabled - Whether the button should be disabled
   */
  setGenerateScopeButtonDisabled(isDisabled: boolean): void {
    this.generateScopeButtonDisabled.next(isDisabled);
  }

  /**
   * ConfirmPmg: Confirms the PMG rerun changes for a project.
   *
   * @param projectId - The project ID to confirm PMG changes for.
   * @returns Observable with the confirmation response.
   */
  confirmPmg(projectId: string,isMasterProject: boolean = false): Observable<any> {
    return this.baseService.put(`/project/confirm-pmg/${projectId}?isMasterProject=${isMasterProject}`, {}, environment.API_MICROSERVICE_URL.PROJECT);
  }

  /**
   * SetShowSyncUpdatesBanner: Updates the visibility state of the sync updates banner.
   *
   * @param showSyncUpdatesBanner - Whether to show the sync updates banner.
   */
  setShowSyncUpdatesBanner = (showSyncUpdatesBanner: boolean) => this.showSyncUpdatesBanner.next(showSyncUpdatesBanner);

  /**
   * GetShowSyncUpdatesBanner: Returns an observable for the sync updates banner visibility state.
   *
   * @returns Observable<boolean> for the sync updates banner visibility state
   */
  getShowSyncUpdatesBanner = (): Observable<boolean> => this.showSyncUpdatesBanner.asObservable();

  /**
   * SetSyncMsg: Sets the sync message to be displayed in the UI.
   *
   * @param syncMsg The new synchronization message to emit.
   */
  setSyncMsg = (syncMsg: string) => this.syncMsg.next(syncMsg);

  /**
   * GetSyncMsg: Returns an observable for the sync message.
   *
   * @returns Observable<string> emitting sync messages
   */
  getSyncMsg = (): Observable<string> => this.syncMsg.asObservable();

  /**
   * SetActivityRedirectData: Sets the activity redirect data to be shared across components.
   *
   * @param activityRedirectData The activity redirect data to emit.
   */
  setActivityRedirectData = (activityRedirectData: any) => this.activityRedirectData.next(activityRedirectData);

  /**
   * GetActivityRedirectData: Returns an observable for the activity redirect data.
   *
   * @returns Observable<any> emitting activity redirect data
   */
  getActivityRedirectData = (): Observable<any> => this.activityRedirectData.asObservable();

  setRejectedMsg(msg: string){
    this.rejectedMsg.next(msg);
  }

  getRejectedMsg(){
    return this.rejectedMsg.asObservable();
  }

  public getProjectId(): Observable<any> {
    return this.projectId.asObservable();
  }

  public setProjectId(data) {
    this.projectId.next(data);
  }

  public getActiveMenu(): Observable<any> {
    return this.activeMenu.asObservable();
  }

  public setActiveMenu(data) {
    this.activeMenu.next(data);
  }

  public getCompletedMenu(): Observable<any> {
    return this.completedMenu.asObservable();
  }

  public setCompletedMenu(data) {
    this.completedMenu.next(data);
  }

  public getScopeDetailsUpdated(): Observable<any> {
    return this.scopeDetailsUpdated.asObservable();
  }

  public setScopeDetailsUpdated(data) {
    this.scopeDetailsUpdated.next(data);
  }

  public setPsgPackagePreference(data) {
    this.psgPackagePreference.next(data);
  }

  public getPsgPackagePreference(): Observable<any> {
    return this.psgPackagePreference.asObservable();
  }

  public setSelectedProjectMenu(data) {
    this.selectedProjectMenu.next(data);
  }

  public getSelectedProjectMenu(): Observable<any> {
    return this.selectedProjectMenu.asObservable();
  }

  public getTitleNamesMaterData(langId){
    return this.baseService.get(`/tools/psg-titles?languageId=${langId}`, environment.API_MICROSERVICE_URL?.ADMIN )
  }

  public getStepNamesMaterData(langId){
    return this.baseService.get(`/tools/psg-translated_title-steps?languageId=${langId}`, environment.API_MICROSERVICE_URL?.ADMIN )
  }

  setSubphaseDeleteData(data) {
    this.subphaseData.next(data);
  }
  getSubphaseDeleteData(): Observable<any> {
    return this.subphaseData.asObservable();
  }

  /**
   * SetPackageMappingDetails: Sets the package mapping details from external-api/get-package-mapping API
   * in the observable.
   *
   * @param packageMappingDetails
   */
  setPackageMappingDetails = packageMappingDetails =>
    this.packageMappingDetails.next(packageMappingDetails);

  /**
   * GetPackageMappingDetailsObservable: Returns an observable for package mapping details so that other
   * components can use it.
   *
   * @returns Observable data for package mapping details
   */
  getPackageMappingDetailsObservable = (): Observable<any> =>
    this.packageMappingDetails?.asObservable();

  /**
   * SetDfteToolsData: Sets the details for DFTE tools from dfteTools API response.
   *
   * @param dfteToolsData
   */
  setDfteToolsData = dfteToolsData =>
    this.dfteToolsData.next(dfteToolsData);

  /**
   * GetDfteToolsDataObservable:Returns an observable for DFTE tools data so that other components can use it.
   *
   * @returns DFTE tools data as an observable
   */
  getDfteToolsDataObservable = (): Observable<any> =>
    this.dfteToolsData?.asObservable();

  /**
   * SetScopeDetails: Sets the project scope details from psg/status API in the observable.
   *
   * @param scopeDetails
   */
  setScopeDetails = (scopeDetails) =>
    this.scopeDetails.next(scopeDetails);

  /**
   * GetScopeDetailsObservable: Returns an observable for project scope details so that other components can use it.
   *
   * @returns Observable data for project scope details
   */
  getScopeDetailsObservable = (): Observable<any> =>
    this.scopeDetails?.asObservable();

  /**
   * Helper method to get the appropriate API URL based on isMasterProject flag and isPMOProject
   */
  private getProjectApiUrl(isMasterProject: boolean): string {
    // Check if this is a PMO project - if so, always use v1 API
    if (this.isPMOProject()) {
      return environment.API_MICROSERVICE_URL.PROJECT;
    }
    // For non-PMO projects, check multiRelease flag and isMasterProject
    return this.isMultiReleaseFeatureFlagEnabled() && isMasterProject 
      ? environment.API_MICROSERVICE_URL.VERSION.V2.PROJECT
      : environment.API_MICROSERVICE_URL.PROJECT;
  }

  private isMultiReleaseFeatureFlagEnabled(): boolean {
    return (this.cryptUtilService.getItem('commonFeatureFlag', 'LOCAL'))?.multiRelease === 'true' ? true : false;
  }

  private isPMOProject(): boolean {
    const commonFeatureFlag = this.cryptUtilService.getItem('commonFeatureFlag', 'LOCAL');
    const initialSetupData = this.cryptUtilService.getItem('INITIAL_SETUP_DATA', 'LOCAL');
    let isPMOProjectFlag = commonFeatureFlag?.isPMOProject;
    
    // Fallback 1: Check from INITIAL_SETUP_DATA.isPMOProject property if it exists
    if ((isPMOProjectFlag === null || isPMOProjectFlag === undefined) && initialSetupData?.isPMOProject !== undefined) {
      isPMOProjectFlag = initialSetupData.isPMOProject ? 'true' : 'false';
    }
    
    // Fallback 2: Check package name from INITIAL_SETUP_DATA (most reliable)
    if ((isPMOProjectFlag === null || isPMOProjectFlag === undefined) && initialSetupData?.selectedPackage) {
      const pkgName = initialSetupData.selectedPackage.pkg_package_name || initialSetupData.selectedPackage.package_name;
      if (pkgName && pkgName.toLowerCase() === 'ascend pmo') {
        isPMOProjectFlag = 'true';
      } else {
        isPMOProjectFlag = 'false';
      }
    }
    
    return isPMOProjectFlag === 'true';
  }

  getScopeDetails(projectId: number, isMasterProject: boolean) {
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.get(`/psg/status/${projectId}`, apiUrl);
  }

  checkGenerateScopeStatus(projectId: number): Observable<any> {
    // Skip for master projects - generate scope only applies to release projects
    const isMasterProject = this.cryptUtilService.getItem('isMasterProject', 'LOCAL');
    if (isMasterProject) {
      return of(null);
    }
    
    return this.baseService.get(`/psg/check-generate-scope-status/${projectId}`, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  updateGenerateScopeButtonState(projectId: number): void {
    this.checkGenerateScopeStatus(projectId).subscribe(
      (response) => {
        // Don't update button state if scope is already generated
        if (response?.data?.psgStatus === 'Y') {
          return;
        }
        
        // Update button state based on whether all required fields are filled
        if (response?.data?.Status === true) {
          this.pubsubService.setTaxonomyStatusReady(true);
        } else {
          this.pubsubService.setTaxonomyStatusReady(false);
        }
      },
      (error) => {
        console.error('Error fetching generate scope status:', error);
      }
    );
  }

  getParentValidity(selectedIndex,stepDetails,stepperContents,isScopeGenerated){
    let result;
    let stepDiff = (selectedIndex + 1) - parseInt(stepDetails?.stepCount?.toString().split('.')[0]);
    if((selectedIndex > 0 && ((parseInt(stepDetails?.stepCount?.toString().split('.')[1]) == stepperContents[selectedIndex-1]?.content?.length && stepDiff == 1) ||
    parseInt(stepDetails?.stepCount?.toString().split('.')[0]) > selectedIndex)) || selectedIndex == 0 || isScopeGenerated){
      result = true;
    }
    else{
      result = false;
    }
    return result;
}

generateScope(projectId: number) {
  this.stepList = [];
  return this.baseService.get(`/psg/generate-scope/${projectId}?steps=${this.stepDetails}`, environment.API_MICROSERVICE_URL?.PROJECT);
}

public crSyncReceived(projectId: number, CrId: number) {
  return this.baseService.get(`/psg/check_cr_sync_received/${projectId}/${CrId}`, environment.API_MICROSERVICE_URL?.PROJECT);
}
  downloadNextGen(projectId, packageId, functionId, featureId, languageId=1, isMasterProject=false) {
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.getBlob(`/psg/download/nexgen/${projectId}/${packageId}/${functionId}/${featureId}/${languageId}`, apiUrl);
  }

  downloadProjectDoc(projectId, packageId, functionId, featureId, isMasterProject=false) {
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.getBlob(`/psg/download/scope/${projectId}/${packageId}/${functionId}/${featureId}/1`, apiUrl);
  }

  upsertDownloadHistory(projectId,documentTypeId) {
    return this.baseService.post(`/psg/upsertdownloadhistory/${projectId}/${documentTypeId}`,{}, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  downloadRICE({ projectId, endpoint, isMasterProject = false }) {
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.getBlob(`/psg/download/technical-scope/${endpoint}/${projectId}`, apiUrl);
  }

  downloadRICEWD({ projectId, endpoint }) {
    return this.baseService.getBlob(`/psg/download/technical-scope-wd/${projectId}?module=technical-scope/${endpoint}`, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  getReviewPageData(projectId: number, isWDPackage?,languageId=1,step=null, isMasterProject=false) {
    // Use the existing helper method that checks both flags (multiReleaseEnabled and isMasterProject)
    const baseUrl = this.getProjectApiUrl(isMasterProject);
    
    let url=`/psg/details/${projectId}/${languageId}?module=review`;
    if(step){
      url=url+`&subStep=${step}`
    }

    if(isWDPackage){
      return this.baseService.get(`${url}&isWDPackage=true`, baseUrl);
    }else{
      return this.baseService.get(`${url}`, baseUrl);
    }
  }

  deleteRelease(subReleaseId: number) {
    return this.baseService.delete(`/project/implementation-approch/${subReleaseId}`, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  restoreRelease(payload: { releaseProjectId: number, status: string, emailId: string }) {
    return this.baseService.post(`/project/releases/restoreRelease`, payload, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  invokeGenerateScope() {
    this.invokeGenerateScopeFunction.emit();
  }

  /**
   * Handle scope generation completion by updating the project data observable
   * This ensures all components listening to projectData get the updated PSG status
   */
  handleScopeGenerationComplete(psgStatus: any, projectId: number): void {
    if (psgStatus) {
      // Get current project data
      const currentData = this.projectData.value;
      // Merge with new PSG status and mark PSG as completed
      const updatedData = {
        ...currentData,
        ...psgStatus,
        isPSGCompleted: 'Y'  // Scope generation completes PSG
      };
      // Update the observable so all subscribers are notified
      this.setProjectDataObs(updatedData);
      // Update scope generated flag
      this.isProjectScopeGenerated.next(true);
      
      // Also fetch fresh project info to ensure we have the latest data
      if (projectId) {
        const isMasterProject = this.cryptUtilService.getItem('isMasterProject', 'LOCAL');
        this.getProjectInfo(projectId, isMasterProject).subscribe(
          (data) => {
            if (data && data[0]) {
              // Update with fresh project info
              this.setProjectDataObs({
                ...this.projectData.value,
                ...data[0]
              });
            }
          },
          (err) => {
            // Silent error, data is already updated with isPSGCompleted: Y
          }
        );
      }
    }
  }
  
  //
  getPageData(urlData: urlDataObject, isv2 = false) {
    const languageId = urlData.languageId || 1;
    let apiUrl;
    let endpoint;
    // Check if this is a PMO project - if so, always use v1 API
    if (this.isPMOProject()) {
      apiUrl = environment.API_MICROSERVICE_URL.PROJECT;
      if (urlData.page && !urlData.isWdPackage) {
        endpoint = `/psg/page/${urlData.projectId}/${languageId}?module=${urlData.module}/${urlData.page}`;
      } else if (urlData.page && urlData.isWdPackage) {
        endpoint = `/psg/page/${urlData.projectId}/${languageId}?module=${urlData.module}/${urlData.page}&isWDPackage=1`;
      } else {
        endpoint = `/psg/page/${urlData.projectId}/${languageId}?module=${urlData.module}`;
      }
    } else if (isv2) {
      // Use v2 base URL and endpoint for non-PMO projects
      apiUrl = environment.API_MICROSERVICE_URL.VERSION.V2.PROJECT;
      if (urlData.page && !urlData.isWdPackage) {
        endpoint = `/psg/page/${urlData.projectId}/${languageId}?module=${urlData.module}/${urlData.page}`;
      } else if (urlData.page && urlData.isWdPackage) {
        endpoint = `/psg/page/${urlData.projectId}/${languageId}?module=${urlData.module}/${urlData.page}&isWDPackage=1`;
      } else {
        endpoint = `/psg/page/${urlData.projectId}/${languageId}?module=${urlData.module}`;
      }
    } else {
      apiUrl = this.getProjectApiUrl(urlData.isMasterProject);
      if (urlData.page && !urlData.isWdPackage) {
        endpoint = `/psg/page/${urlData.projectId}/${languageId}?module=${urlData.module}/${urlData.page}`;
      } else if (urlData.page && urlData.isWdPackage) {
        endpoint = `/psg/page/${urlData.projectId}/${languageId}?module=${urlData.module}/${urlData.page}&isWDPackage=1`;
      } else {
        endpoint = `/psg/page/${urlData.projectId}/${languageId}?module=${urlData.module}`;
      }
    }
    return this.baseService.get(endpoint, apiUrl);
  }

  getProjectData(urlData: urlDataObject, isv2 = true) {
    urlData.isMasterProject = this.cryptUtilService.getItem('isMasterProject', 'LOCAL') || false;
    const languageId = urlData.languageId || 1;
    const apiUrl = this.getProjectApiUrl(urlData.isMasterProject);
    
    const endpoint = urlData.page 
      ? `/psg/details/${urlData.projectId}/${languageId}?module=${urlData.module}/${urlData.page}${urlData.package ? '&' + urlData.package : ''}`
      : `/psg/details/${urlData.projectId}/${languageId}/?module=${urlData.module}${urlData.package ? '&' + urlData.package : ''}`;
    
    return this.baseService.get(endpoint, apiUrl);
  }

  getMasterProjectData(urlData: urlDataObject, isv2 = true) {
    urlData.isMasterProject = this.cryptUtilService.getItem('isMasterProject', 'LOCAL') || false;
    const languageId = urlData.languageId || 1;
    const apiUrl = this.getProjectApiUrl(true);
    
    const endpoint = urlData.page 
      ? `/psg/details/${urlData.projectId}/${languageId}?module=${urlData.module}/${urlData.page}${urlData.package ? '&' + urlData.package : ''}`
      : `/psg/details/${urlData.projectId}/${languageId}/?module=${urlData.module}${urlData.package ? '&' + urlData.package : ''}`;
    
    return this.baseService.get(endpoint, apiUrl);
  }

  saveProjectData(Data) {
    Data.isMasterProject = this.cryptUtilService.getItem('isMasterProject', 'LOCAL') || false;
    this.stepList.push(Data.stepPath);
    delete Data.stepPath;
    this.stepDetails = this.stepList?.filter((value, index) => this.stepList.indexOf(value) === index).toString();
    const apiUrl = this.getProjectApiUrl(Data.isMasterProject);
    return this.baseService.post(`/psg/save`, Data, apiUrl);
  }

  createPathForUpdateNotification(path) {
    let splitUrl = path?.split("/");
    const len = splitUrl.length;
    let toReturnUrl = "";
    return toReturnUrl += `${splitUrl[len-3]}/${splitUrl[len-2]}`
  }

  /**
   * GetToolsCategoryConstant: It's used to fetch
   * psg sidebar tool categories
   *
   * @param languageId Number
   * @returns Array of Objects
   */
  getToolsCategoryConstant = (languageId: number): Observable<any> =>
    of(TOOLS_CATEGORY_LIST);

  getFeatureList() {
    return this.baseService.get('/package/features/0', this.API_MICROSERVICE_ADMIN);
  }

  getProjectPref(package_id: number, function_id: number, feature_id: number, langId) {
    return this.baseService.get(`/package/preferences/${package_id}/${function_id}/${feature_id}?languageId=${langId}`, this.API_MICROSERVICE_ADMIN);
  }

  getToolsCategory(languageId){
    return this.baseService.get(`/tools/tools-category?languageId=${languageId}`, this.API_MICROSERVICE_ADMIN);
  }

  getToolsMarketSolutions(projectId, packageId, toolCategoryId){
    return this.baseService.get(`/project/market-solutions/${projectId}/${packageId}/${toolCategoryId}`, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  getTraditionalAmplifier(projectId, packageId, toolCategoryId){
    return this.baseService.get(`/project/traditional-amplifiers/${projectId}/${packageId}/${toolCategoryId}`, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  getGenAITools(projectId, packageId, toolCategoryId, languageId){
    return this.baseService.get(`/project/genai-amplifiers/${projectId}/${packageId}/${toolCategoryId}?languageId=${languageId}`, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  /**
   * GetPrimaryTools: It'll return global Primary Tools
   *
   * @param projectId Number
   * @param packageId Number
   * @param languageId Number
   * @returns Object
   */
  getPrimaryTools = (
    projectId: number,
    packageId: number,
    languageId: number
  ): Observable<any> =>
    this.baseService.get(
      `/project/primary-tools/${projectId}/${packageId}?languageId=${languageId}`,
      environment.API_MICROSERVICE_URL?.PROJECT
    );

  /**
   * GetAdditionalTools: It'll return global Additional
   * Tools
   *
   * @param projectId Number
   * @param packageId Number
   * @param languageId Number
   * @returns Object
   */
  getAdditionalTools = (
    projectId: number,
    packageId: number,
    languageId: number
  ): Observable<any> =>
    this.baseService.get(
      `/project/additional-tools/${projectId}/${packageId}?languageId=${languageId}`,
      environment.API_MICROSERVICE_URL?.PROJECT
    )
  /**
   * SaveAllTools: It'll save all primary & additional
   * tools activities
   *
   * @param projectId Number
   * @param body ISaveToolDetails
   * @returns Object
   */
  saveAllTools = (
    projectId: number,
    body: ISaveToolDetails[]
  ): Observable<any> =>
    this.baseService.post(
      `/project/tools/${projectId}`,
      body,
      environment.API_MICROSERVICE_URL?.PROJECT
    )

  /**
   * GetToolProvisioningDependencies: Retrieves tool provisioning
   * dependencies for a specific package.
   *
   * @param packageId - The unique identifier of the package to get
   * provisioning dependencies for.
   *
   * @returns An observable containing the tool provisioning dependency data.
   */
  getToolProvisioningDependencies = (packageId: number) =>
    this.baseService.get(
      `/tools/tool-provisioning-dependencies?packageId=${packageId}`,
      environment.API_MICROSERVICE_URL?.ADMIN
    );

  sendGenAIEnabledEmail(projectId) {
    return this.baseService.post(`/project/sendGenAIAmplifiersEmail/${projectId}`, {}, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  saveGenAiTools(projectId, toolCategoryId, body){
    return this.baseService.post(`/project/genaitools/${projectId}/${toolCategoryId}`, body, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  saveTools(projectId, toolCategoryId, body){
    return this.baseService.post(`/project/tools/${projectId}/${toolCategoryId}`, body, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  saveProjectToolConsent(projectId, toolId, body?){
    return this.baseService.post(`/project/saveConsent/${projectId}/${toolId}`, body, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  getFeatureData(featureId: number) {
    return this.baseService.get(`/package/features/${featureId}`, this.API_MICROSERVICE_ADMIN);
  }

  getProjectInfo(projectId, isMasterProject = false) {
    let languageId = 1;
    // If isMasterProject is not explicitly passed, read from storage
    if (isMasterProject === false && arguments.length < 2) {
      isMasterProject = this.cryptUtilService.getItem('isMasterProject', 'LOCAL') || false;
    }
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.get(`/project/${projectId}/${languageId}`, apiUrl);
  }

  /**
   * Fetches project info from a specific region's API
   * @param projectId - Project ID to fetch
   * @param isMasterProject - Whether this is a master project (determines v1 vs v2 API)
   * @param apiBaseUrl - Region-specific API base URL (e.g., EU API base URL)
   */
  getProjectInfoByRegion(projectId: string, isMasterProject: boolean, apiBaseUrl: string) {
    const languageId = 1;
    // Determine version suffix based on isMasterProject and multiRelease flag
    const isMultiRelease = this.isMultiReleaseFeatureFlagEnabled();
    const versionPath = isMultiRelease && isMasterProject ? '/Project/api/v2' : '/Project/api/v1';
    const fullApiUrl = `${apiBaseUrl}${versionPath}`;
    return this.baseService.get(`/project/${projectId}/${languageId}`, fullApiUrl);
  }
  
  getInitProjectInfo(projectId, isMasterProject = false) {
    let languageId = 1;
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.get(`/project/${projectId}/${languageId}`, apiUrl);
  }

  getProjectBasicInfo(projectId, isMasterProject=false) {
    let languageId = 1;
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.get(`/project/basic/${projectId}/${languageId}`, apiUrl);
  }

  getPckgResponse(res) {
    res?.data?.forEach((item) => {
      item.translatedName = item.package_method_nm;
      item.package_id = item.package_method_id;
      item.project_mode = item.is_project_mode_set ? 'Y' : 'N';
    })
    return res;
   }

  fetchActivePackages() {
    return this.baseService.get(`/package/package-detail?statusId=1`, environment.API_MICROSERVICE_URL?.METHOD).map(res => this.getPckgResponse(res));
  }

  getActivePackage(): Observable<any> {
    return this.activePackages.asObservable();
  }

  setActivePackage(obj: any) {
    this.activePackages.next({ package: obj });
  }

  getCurrentPageDetails() {
    // Work in progress
    let currentpage = '';
    let previouspage = '';
    let nextpage = '';
    let currentpath = '';
    let nextpath = '';
    let previouspath = '';
    let placeholder_text = '';
    let parentIndex = 0;
    let childIndex = 0;
    let data = this.cryptUtilService.getItem('stepperContents', 'SESSION');
    let translatedName  = ''
    if (!data) return;
    let url = this.router.url.split('/');
    url.splice(1, 1);
    url.pop();
    const currentrout = url.join('/');


    let activeParent: any = data.find((item: any) => {
      if (item?.content.length > 0) {
        return item.content.find((elm) => {
          return elm.path.includes(currentrout)
        });
      } else {
        return data.find((elm) => {
          if (elm?.path) {
            return elm?.path.includes(currentrout)
          }
        });
      }
    });
    if(!activeParent) return;

    let activePage = activeParent.content.find((elm) => {
      return elm.path.includes(currentrout)
    });

    if (activeParent && activePage) {
      if(!activeParent?.translated_title)
       currentpage = ' ';
      else
      currentpage = activeParent?.translated_title;
      if (activePage?.translatedName) {
        currentpage += ' / ' + activePage?.translatedName;
        translatedName = activePage?.translatedName
      }
      currentpath = activePage?.path;
      parentIndex = data[activeParent?.id - 1]?.id - 1;
      childIndex = activePage.index - 1;
      placeholder_text = activePage?.placeholder_text;
      if (activeParent?.content.length === activePage.index) {
        previouspage = activeParent?.translated_title + ' / ' + activeParent?.content[activePage.index - 2]?.translatedName;
        previouspath = activeParent?.content[activePage.index - 2]?.path;

        if(!previouspath && (activeParent?.id - 2)>=0){
          let temp = data[activeParent?.id - 2].content[data[activeParent?.id - 2].content.length - 1];
          previouspath = temp?.path;
        }

        if (activeParent.id === data[data.length - 1].id) {
          nextpage = activeParent?.translated_title + ' / ' + activePage?.translatedName;
          nextpath = currentpath = activePage?.path;
        } else {
          let temp = null;
          if (data[activeParent?.id].content?.length > 1) {
            temp = data[activeParent?.id].content[0];
            nextpage = data[activeParent?.id]?.translated_title + ' / ' + temp?.translatedName;
            nextpath = temp?.path;
          }
          else if (data[activeParent?.id].content?.length == 1) {
            temp = data[activeParent?.id].content[0];
            nextpage = data[activeParent?.id]?.translated_title;
            nextpath = temp?.path;
          } else {
            temp = data[activeParent?.id];
            nextpage = data[activeParent?.id]?.translated_title;
            nextpath = temp?.path;
          }
        }
      } else if (activePage.index === 1) {
        if (activeParent.id === 1) {
          previouspage = activeParent?.translated_title + ' / ' + activePage?.translatedName;
          previouspath = activePage?.path;
        } else {
          let temp = data[activeParent?.id - 2].content[data[activeParent?.id - 2].content.length - 1];
          previouspage = data[activeParent?.id - 2]?.translated_title + ' / ' + temp?.translatedName;
          previouspath = temp?.path;
        }
        nextpage = activeParent?.translated_title + ' / ' + activeParent?.content[activePage.index]?.translatedName;
        nextpath = activeParent?.content[activePage.index]?.path;
      } else {
        previouspage = activeParent?.translated_title + ' / ' + activeParent?.content[activePage.index - 2]?.translatedName;
        previouspath = activeParent?.content[activePage.index - 2]?.path;
        nextpage = activeParent?.translated_title + ' / ' + activeParent?.content[activePage.index]?.translatedName;
        nextpath = activeParent?.content[activePage.index]?.path;
      }
    }

    return {
      currentpage: currentpage,
      currentpath: currentpath,
      placeholder_text: placeholder_text,
      previouspage: previouspage,
      previouspath: previouspath,
      nextpage: nextpage,
      nextpath: nextpath,
      parentIndex: parentIndex,
      childIndex: childIndex,
      translatedName: translatedName
    }
  }


  getScopeUpdate(): Observable<boolean> {
    return this.updateScopeNotify.asObservable();
  }

  setScopeUpdate(scopeData) {
    this.updateScopeNotify.next(scopeData);
  }

  addUpdateExternalUserFlag(data?) {
    if (data) {
      this.cryptUtilService.removeItem("external-user-data-project", "SESSION");
        return this.cryptUtilService.setItem(
          'external-user-data-project',
          { isExternalUser: data?.isExternalUser ?? false, pages: data?.pages },
          'SESSION'
        );
    } else {
    return this.cryptUtilService.getItem('external-user-data-project', 'SESSION');
    }
  }

  getUserInfo() {
    return this.cryptUtilService.getItem('userInfo', 'SESSION');
  }

  getExternalUserPermissions(projectId, userId, langId) {
      //Here we are removing the language Id from the user permission URL as it is not required -  #2865340
    const externalUserURL = `/user/User-Permissions/Project?project_id=${projectId}&email_id=${userId}`;
    return this.baseService.get(externalUserURL, this.userMicroserviceUrl);
  }

  validateComplexData(formGroup,formElements,isSomeSelected ,isAllRowsSelected,selectedIndexes,service_data):boolean{
    if (isSomeSelected || isAllRowsSelected) {
      let count = 0;
      selectedIndexes.forEach((i) => {
        const serviceElem = formGroup.get(formElements).get([i]);
        if (serviceElem.get('complexity').value == '' && (serviceElem.get('quantity')?.value == null || serviceElem.get('quantity')?.value == '' || serviceElem.get('quantity')?.value < 0 || serviceElem.get('quantity')?.value > 1000000) && serviceElem.get('complexwithoutquantity').value == '') {
          service_data[i].fieldError = true;
          count++;
        } else {
          service_data[i].fieldError = false;
        }
      })
      if (count == 0) return true;
      else return false;
    }
    else return true;
  }

  validateComplexForDeployment(formGroup,formElements,isSomeSelected ,isAllRowsSelected,selectedIndexes,service_data):boolean{
    if (isSomeSelected || isAllRowsSelected) {
      let count = 0;
      selectedIndexes.forEach((i) => {
        const serviceElem = formGroup.get(formElements).get([i]);
        if (serviceElem.get('complexity').value == '' && (serviceElem.get('quantity')?.value == null || serviceElem.get('quantity')?.value == '' || serviceElem.get('quantity')?.value < 0 || serviceElem.get('quantity')?.value > 1000000)) {
          service_data[i].fieldError = true;
          count++;
        } else {
          service_data[i].fieldError = false;
        }
      })
      if (count == 0) return true;
      else return false;
    }
    else return true;
  }

  validateQuantityData(formGroup,formElements,isSomeSelected ,isAllRowsSelected,selectedIndexes,service_data):boolean{
    if (isSomeSelected || isAllRowsSelected) {
      let count = 0;
      selectedIndexes.forEach((i) => {
        const serviceElem = formGroup.get(formElements).get([i]);
        if (serviceElem.get('quantity').value == '' || serviceElem.get('quantity').value < 0  || serviceElem.get('quantity').value > 1000000) {
          service_data[i].fieldError = true;
          count++;
        }
        else {
          service_data[i].fieldError = false;
        }
      })
      if (count == 0) return true;
      else return false;
    }
    else return true;
  }

  getExternalUserDataUpdated(): Observable<any> {
    return this.isExternalUserDataUpdated.asObservable();
  }

  setExternalUserDataUpdated(data) {
    this.isExternalUserDataUpdated.next(data);
  }

  downloadCR(payload){
    let languageId=1;
    return this.baseService.getBlobAsJson(`/psg/download-cr-archive/${payload?.projectId}/${languageId}`, payload, environment.API_MICROSERVICE_URL?.PROJECT).subscribe(
      (data: any) => {
        const dataHeaders = data.headers.get('content-disposition');
        const fileName = JSON.parse(dataHeaders?.split('=')[1]);
        const type = data.headers.get('content-type');
        const blob = new Blob([data.body], { type });
        saveAs(blob, fileName);
      },
      err => {
        console.log(err);
      });
  }

  getCompletedCRDetails(projectId, data?){
    return this.baseService.post(`/psg/change-request-archive/${projectId}`, data, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  getChangeRequestStatus(projectId) {
    const cacheKey = `${projectId}`;
    if (!this.changeRequestCache.has(cacheKey)) {
      const request$ = this.baseService.get(`/psg/scope_change_request/details/${projectId}`, environment.API_MICROSERVICE_URL?.PROJECT).pipe(
        shareReplay(1)
      );
      this.changeRequestCache.set(cacheKey, request$);
    }
    return this.changeRequestCache.get(cacheKey);
  }

  clearChangeRequestCache(projectId?: string) {
    if (projectId) {
      this.changeRequestCache.delete(projectId);
    } else {
      this.changeRequestCache.clear();
    }
  }

  setDisplayCrTooltipText(data) {
    this.displayTooltipText.next(data);
  }

  getDisplayCrTooltipText(): Observable<string> {
    return this.displayTooltipText.asObservable();
  }

  getActivityRedirecDetails(projectId) {
    return this.baseService.get(`/psg/getDetailsForActivityRedirect/${projectId}`, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  getChangeRequestData(): Observable<any> {
    return this.CRData as Observable<any>;
  }

  setChangeRequestData(data) {
    this.CRData.next(data);
  }

  getProjectDataObs(): Observable<any> {
    return this.projectData as Observable<any>;
  }

  setProjectDataObs(data) {
    this.projectData.next(data);
  }

  getProjectScope(): Observable<boolean> {
    return this.isProjectScopeGenerated as Observable<boolean>;
  }

  setProjectScope(data: boolean) {
    this.isProjectScopeGenerated.next(data);
  }

  getActivityData(projectId, packageId, functionId, phaseName, stopName) {
    return this.baseService.get(`/activities/activity-phase-stops?projectId=${projectId}&packageId=${packageId}&functionId=${functionId}&phaseName=${phaseName}&stopName=${stopName}`)
  }


  getJiraStatusforCr(projectId) {
    return this.baseService.get(`/psg/isReadyToRaiseCr/${projectId}`, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  projectScopeUpdated(projectId, updateScopeflag) {
    return this.baseService.put(`/psg/change_request_scope_update/${projectId}/${updateScopeflag}`, {}, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  getProcessPageData(project_id, l1_id?, l2_id?, l1?, l2?,languageId?) {
    let url = `/psg/scope-process/${project_id}`;
    if (l1_id) {
      url = url + `?l1=${encodeURIComponent(l1)}&l1_cc_id=${l1_id}`;
      if (l2_id) {
        url = url + `&l2=${encodeURIComponent(l2)}&l2_cc_id=${l2_id}`;
      }
    }
    if(languageId){
      url += (l1_id ? `&` : `?`)+`languageId=${languageId}`;
    }
    return this.baseService.get(url, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  getMasterProcessPageData(project_id, l1_id?, l2_id?, l1?, l2?,languageId?) {
    let url = `/psg/scope-process/${project_id}`;
    if (l1_id) {
      url = url + `?l1=${encodeURIComponent(l1)}&l1_cc_id=${l1_id}`;
      if (l2_id) {
        url = url + `&l2=${encodeURIComponent(l2)}&l2_cc_id=${l2_id}`;
      }
    }
    if(languageId){
      url += (l1_id ? `&` : `?`)+`languageId=${languageId}`;
    }
    // Use the helper method to determine correct API URL based on PMO project and multi-release feature flag
    const apiUrl = this.getProjectApiUrl(true);
    return this.baseService.get(url, apiUrl);
  }

  saveProjectProcessData(data, projectId,isMasterProject?) {
    this.stepList.push(data.stepPath);
    delete data.stepPath;
    this.stepDetails = this.stepList?.filter((value, index) => this.stepList.indexOf(value) === index).toString();
    // Check if this is a PMO project - if so, always use v1 API
    const apiUrl = this.isPMOProject() 
      ? environment.API_MICROSERVICE_URL?.PROJECT
      : (isMasterProject ? environment.API_MICROSERVICE_URL?.VERSION.V2.PROJECT : environment.API_MICROSERVICE_URL?.PROJECT);
    return this.baseService.put(`/psg/save-psg-processes/${projectId}`, data, apiUrl);
  }

  saveProjectOfferingData(Data) {
    this.stepList.push(Data.stepPath);
    delete Data.stepPath;
    this.stepDetails = this.stepList?.filter((value, index) => this.stepList.indexOf(value) === index).toString();
    const isMasterProject = Data.data && Data.data[0] && Data.data[0].isMasterProject;
    // Check if this is a PMO project - if so, always use v1 API
    const apiUrl = this.isPMOProject() 
      ? environment.API_MICROSERVICE_URL?.PROJECT
      : (isMasterProject ? environment.API_MICROSERVICE_URL?.VERSION.V2.PROJECT : environment.API_MICROSERVICE_URL?.PROJECT);
    return this.baseService.put(`/project`, Data, apiUrl);
  }

  resetSurvey(projectId, isMasterProject = false) {
    // Check if this is a PMO project - if so, always use v1 API
    const apiUrl = this.isPMOProject() 
      ? environment.API_MICROSERVICE_URL?.PROJECT
      : (isMasterProject ? environment.API_MICROSERVICE_URL?.VERSION.V2.PROJECT : environment.API_MICROSERVICE_URL?.PROJECT);
    return this.baseService.delete(`/psg/pmg-questions-answers/${projectId}`, apiUrl);
  }

  restoreSurvey(projectId) {
    return this.baseService.post(`/psg/discard-pmg-survey/${projectId}`, {}, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  downloadProjectReport(packageMethodId: number, payload: any): void {
    this.baseService.getBlobAsJson(`pmg/download/${packageMethodId}`, payload, environment.API_MICROSERVICE_URL?.METHOD)
      .subscribe({
        next: (response: any) => {
          const dataHeaders = response.headers.get('content-disposition');
          const fileName = JSON.parse(dataHeaders?.split('=')[1]);
          const type = response.headers.get('content-type');
          const blob = new Blob([response.body], { type });
          saveAs(blob, fileName);
        },
        error: (err) => {
          console.log(err);
        }
      });
  }

  submitApprove(projectId: number,isMasterProject: boolean = false) {
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    return this.baseService.patchCustom(`/project/projectStatus/${projectId}`, apiUrl);
  }

  setProjectStatus(data) {
		this.projectStatus.next(data);
	}

	getProjectStatus(): Observable<any> {
		return this.projectStatus.asObservable();
	}

  setDraftStatus(data) {
    this.isDraftCheck.next(data);
  }

  getDraftStatus(): Observable<any> {
    return this.isDraftCheck.asObservable();
  }

  getIndustryPrintsById(industry_print_id: number) {
    return this.baseService.get(`/external-api/getIndustryPrint/?industry_print_id=${industry_print_id}`, environment.API_MICROSERVICE_URL.PACKAGE_CONTENT)
  }

  requestToolsInstance(projectId: number): any {
    const url = `/tools/dfteTools/${projectId}`;
    return this.baseService.get(url,this.projectMicroservice);
  }

  saveOtherTools(projectId, body){
    return this.baseService.post(`/project/tool-provision/${projectId}`, body, environment.API_MICROSERVICE_URL?.PROJECT);
  }

  getInitialDataReady() {
    return this.initialDataSet.asObservable();
  }

  setInitialDataReady(data) {
    this.initialDataSet.next(data);
  }

  checkReleasePlanFinalizeStatus(projectId: number, isMasterProject: boolean = false): Observable<any> {
    const apiUrl = this.getProjectApiUrl(isMasterProject);
    const url = `/psg/check-release-plan-status/${projectId}`;
    return this.baseService.get(url, apiUrl);
  }

  getHasReleaseDataForMasterProject(masterProjectId: number): Observable<any> {
    // return of({
    //   hasReleaseData: false
    // });
    // Check if this is a PMO project - if so, always use v1 API
    const apiUrl = this.isPMOProject() 
      ? environment.API_MICROSERVICE_URL.PROJECT
      : environment.API_MICROSERVICE_URL?.VERSION.V2.PROJECT;
    return this.baseService.get(`/project/check-process-map-status/${masterProjectId}`, apiUrl);
  }

  getHasReleaseDataForMasterProjectWD(masterProjectId: number): Observable<any> {
    // return of({
    //   data: { hasReleaseData: true }
    // });
    // Check if this is a PMO project - if so, always use v1 API
    const apiUrl = this.isPMOProject() 
      ? environment.API_MICROSERVICE_URL.PROJECT
      : environment.API_MICROSERVICE_URL?.VERSION.V2.PROJECT;
    return this.baseService.get(`/project/check-module-status/${masterProjectId}`, apiUrl);
  }
}
